import React from 'react';
import { gql } from '@apollo/client';
import {
  ArrayField,
  ChipField,
  Datagrid,
  TextField,
  DateField,
  NumberField,
  SingleFieldList,
  FunctionField,
} from 'react-admin';
import LinkField from 'components/LinkField';
import List from 'components/List';
import { IdFilter } from 'components/Filter';
import MoneyField from 'components/MoneyField';
import DateTimeField from 'components/DateTimeField';
import IdField from 'components/IdField';
import TravelProfileRemainingStepsField from 'components/TravelProfileRemainingStepsField';
const PermanentRequestsFilter = [<IdFilter alwaysOn />];

const PermanentRequestsList = () => (
  <List
    exporter={false}
    filters={PermanentRequestsFilter}
    title="Permanent Requests"
    data-testid="permanent-request-list"
  >
    <Datagrid bulkActionButtons={false}>
      <IdField resource="PermanentRequest" />
      <LinkField label="Pro ID" source="professional.id" />
      <TextField label="Pro Name" source="professional.account.name" />
      <ArrayField label="Position(s)" source="assignment.positions">
        <SingleFieldList linkType={false}>
          <ChipField source="display_name" />
        </SingleFieldList>
      </ArrayField>
      <NumberField label="Travel Profile %" source="travel_profile_strength" />
      <TravelProfileRemainingStepsField />
      <DateField label="Applied On Date" source="created_at" />
      <LinkField label="Assignment ID" source="assignment.id" typename="PermanentPlacement" />
      <FunctionField
        label="TAM"
        render={(v) => {
          const tam = v?.assignment?.recruiter;
          if (!tam) {
            return null;
          }
          return <LinkField to={`/Administrator/${tam.id}/show`}>{tam.account?.name}</LinkField>;
        }}
      />
      <TextField label="VMS Type" source="assignment.company.vms_type.label" />
      <TextField label="VMS Order #" source="assignment.order_number" />
      <TextField label="Health System" source="assignment.company.health_system.label" />
      <TextField label="Company Name" source="assignment.company.name" />
      <DateField label="VMS Profile Submitted At" source="vms_profile_submitted_at" />
      <DateField label="VMS Application Submitted At" source="vms_application_submitted_at" />
      <TextField label="Request Status" source="status" />
      <TextField label="VMS Profile" source="vms_profile_status" />
      <TextField label="VMS Application" source="vms_application_status" />
      <MoneyField label="TCV" source="assignment.charge_blended_hourly_rate_cents" />
      <DateTimeField label="Starts Date" format="MM/dd/yyyy" source="assignment.starts_date" />
    </Datagrid>
  </List>
);

PermanentRequestsList.query = gql`
  query GET_LIST($input: AssignmentRequestQueryInput) {
    _permanentRequestsMeta(input: $input) {
      count
    }
    permanentRequests(input: $input) {
      id
      vms_profile_status
      vms_application_status
      vms_profile_submitted_at
      vms_application_submitted_at
      created_at
      status
      assignment_id
      assignment {
        id
        starts_date
        status
        is_w2
        order_number
        charge_blended_hourly_rate_cents
        positions {
          id
          display_name
        }
        company {
          id
          name
          vms_type {
            id
            label
          }
          health_system {
            id
            label
          }
          msp {
            id
            label
          }
        }
        primary_location {
          id
          timezone_lookup
          covid_requirement
        }
        number_of_openings
        recruiter {
          id
          account {
            name
          }
        }
      }
      professional_id
      professional {
        id
        account {
          agency_id
          agency {
            name
          }
          id
          company_id
          company {
            name
          }
          name
          first_name
          last_name
          phone_number
        }
        profile_strength
        recruiter_id
        recruiter {
          id
          account {
            id
            name
          }
        }
        markets {
          id
          name
          display_name
        }
      }
      offer_source
      phone_screening_request {
        id
        assignment_request {
          id
          professional_id
        }
        confirmed_phone_screening_time
        masked_professional_phone_number
        status
      }
      has_missing_work_history
      has_unexplained_work_gaps
      has_missing_education
      has_missing_ehr_software
      has_missing_references
      has_missing_availability
      has_missing_identity
      travel_profile_strength
    }
  }
`;

export default PermanentRequestsList;
